import { yupResolver } from '@hookform/resolvers/yup';
import { useOktaAuth } from '@okta/okta-react';
import { useQueryClient } from '@tanstack/react-query';
import {
    Badge,
    Button,
    ButtonSizes,
    ButtonVariants,
    LoadingIndicator,
    Modal,
    rem,
    SpinnerSizes,
} from '@triumph/design-system';
import { Tag } from 'primereact/tag';
import React, { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { useToast } from '@/components/hooks/useToast';
import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import {
    FACTORING_LOADPAY,
    ProductNameFields,
    ProductNames,
} from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import { getApplicant } from '@/components/shared/api/endpoints/applicant';
import { useGenerateRecoveryLinkMutation } from '@/components/shared/api/mutations/applicant';
import { useApplicantAssignees } from '@/components/shared/api/queries/assignees';
import { useConditionsQuery } from '@/components/shared/api/queries/conditions';
import { AssignCell } from '@/components/shared/components/AssignCell';
import { TAB_BADGE_ACTIVE_STYLE } from '@/components/shared/constants/conditionsBadge';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { Feature, PERMISSION_DENIED_TITLE } from '@/components/shared/constants/features';
import {
    EVENT_LOG_NAMES,
    getLoggingContext,
    honeybadgerLogEvent,
} from '@/components/shared/utils/logging';
import { formatDateWithTimezone, formatOrdinal } from '@/components/shared/utils/utils';

import {
    InfoTitle,
    RecentlyCompletedRequestBanner,
    StatusGrid,
    StatusGridCell,
    StatusWrapper,
    StatusWrapperRow,
} from '../../Applicant.StyledComponents';
import {
    ApplicantTabId,
    DecisionDetailProps,
    DecisionDetailStatuses,
    FactoringProduct,
    InformationRequest,
    LoadPayProduct,
} from '../../Applicant.types';
import { getStatusVariant, getSubmittedOrdinals } from '../../Applicant.utils';
import { validateDecisionForm, validateRequestInfoForm } from './ApplicantStatus.schemas';
import {
    ModalContainer,
    PendingRequestBadge,
    StatusInfoButton,
    StatusInfoModalDescription,
    StatusInfoModalRow,
    ViewRequestModalCard,
    ViewRequestModalMessage,
    ViewRequestModalProductTag,
} from './ApplicantStatus.StyledComponents';
import { EditModal } from './EditModal';
import { ProductDecisionModal } from './ProductDecisionModal';
import { RequestInfoDrawer } from './RequestInfoDrawer';
import { StatusInfoModal } from './StatusInfoModal';

type ActionType = 'Decision' | 'MoreInfo' | 'Edit';

const eventNameMap: Record<ActionType, Record<ProductNames, EVENT_LOG_NAMES>> = {
    Decision: {
        [ProductNames.FACTORING]: EVENT_LOG_NAMES.FactoringDecision,
        [ProductNames.LOADPAY]: EVENT_LOG_NAMES.LoadPayDecision,
        [ProductNames.TCSFUEL]: EVENT_LOG_NAMES.TcsFuelDecision,
    },
    MoreInfo: {
        [ProductNames.FACTORING]: EVENT_LOG_NAMES.FactoringRequestInfo,
        [ProductNames.LOADPAY]: EVENT_LOG_NAMES.LoadPayRequestInfo,
        [ProductNames.TCSFUEL]: EVENT_LOG_NAMES.TcsFuelRequestInfo,
    },
    Edit: {
        [ProductNames.FACTORING]: EVENT_LOG_NAMES.FactoringEdit,
        [ProductNames.LOADPAY]: EVENT_LOG_NAMES.LoadPayEdit,
        [ProductNames.TCSFUEL]: EVENT_LOG_NAMES.TcsFuelEdit,
    },
};

export const ApplicantStatus = () => {
    const { applicant, applicantPartnerId, applicantProductNames, applicantId } =
        useApplicantContext();
    const { authState } = useOktaAuth();
    const currentUserEmail = useCurrentUserEmail();
    const { hasFeature } = usePermissions();
    const {
        isV2InformationRequestEnabled,
        isPostApprovalInformationRequestsEnabled,
        isV2InformationRequestPreApprovalEnabled,
        isCancelInformationRequestEnabled,
    } = useFeatureFlags();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();

    const navigateToConditionsTab = () => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', ApplicantTabId.Conditions);
        setSearchParams(next, { replace: true });
    };

    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [decisionModalProduct, setDecisionModalProduct] = useState<ProductNames>(
        ProductNames.FACTORING,
    );
    const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
    const [requestInfoDefaultProduct, setRequestInfoDefaultProduct] = useState<string>('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [editModalDefaultProduct, setEditModalDefaultProduct] = useState<string>('');
    const [editModalRecoveryLink, setEditModalRecoveryLink] = useState<string>('');
    const [editModalPasscode, setEditModalPasscode] = useState<string>('');

    const [showViewRequestModal, setShowViewRequestModal] = useState(false);
    const [viewRequestModalRequests, setViewRequestModalRequests] = useState<InformationRequest[]>(
        [],
    );

    const [showStatusInfoModal, setShowStatusInfoModal] = useState(false);
    const [statusInfoProductStatus, setStatusInfoProductStatus] = useState<string>('');
    const [statusInfoStatusLabel, setStatusInfoStatusLabel] = useState<string>('');
    const [statusInfoDecisionDetail, setStatusInfoDecisionDetail] = useState<
        DecisionDetailProps | undefined
    >(undefined);

    const productDecisionFormMethods = useForm({
        defaultValues: {
            status: '',
            reason: '',
            notes: '',
        },
        resolver: yupResolver(validateDecisionForm()),
    });

    const requestInfoFormMethods = useForm({
        defaultValues: {
            product: [],
            notes: '',
        },
        resolver: yupResolver(validateRequestInfoForm()),
    });

    // Assignee hooks — poll every 30s so the assigned display stays live across reviewer windows
    const { data: assignees } = useApplicantAssignees(applicantId!, { refetchInterval: 30_000 });

    const { data: conditionsData } = useConditionsQuery({
        applicantId: applicantId ?? '',
        enabled: Boolean(applicantId),
    });

    const openConditionsByProduct = useMemo(() => {
        const counts: Record<ProductNames, number> = {
            [ProductNames.FACTORING]: 0,
            [ProductNames.LOADPAY]: 0,
            [ProductNames.TCSFUEL]: 0,
        };
        (conditionsData ?? [])
            .filter((c) => !c.resolved && !c.isDeleted)
            .forEach((c) => {
                c.products?.forEach((p) => {
                    if (p in counts) counts[p] += 1;
                });
            });
        return counts;
    }, [conditionsData]);

    const activeRequestsByProduct = useMemo(() => {
        const map = new Map<ProductNames, InformationRequest[]>();
        (applicant?.informationRequests ?? [])
            .filter((r) => r.isActive && r.isPendingApplicantResponse)
            .forEach((r) => {
                const key = r.product.toUpperCase() as ProductNames;
                if (!map.has(key)) map.set(key, []);
                map.get(key)!.push(r);
            });
        return map;
    }, [applicant?.informationRequests]);

    const requestInfoActiveRequests = useMemo(
        () => [
            ...(activeRequestsByProduct.get(ProductNames.FACTORING) ?? []),
            ...(activeRequestsByProduct.get(ProductNames.LOADPAY) ?? []),
        ],
        [activeRequestsByProduct],
    );

    const recentlyCompletedRequestMap = useMemo(() => {
        const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000);
        const requestMap = new Map<ProductNames, InformationRequest>();
        const requests = applicant?.informationRequests ?? [];
        const mostRecentByProduct = new Map<string, InformationRequest>();
        requests.forEach((r) => {
            const key = r.product.toUpperCase();
            const existing = mostRecentByProduct.get(key);
            if (!existing || new Date(r.dateModified) > new Date(existing.dateModified)) {
                mostRecentByProduct.set(key, r);
            }
        });
        mostRecentByProduct.forEach((r, key) => {
            if (!r.isPendingApplicantResponse && new Date(r.dateModified) >= threeDaysAgo) {
                requestMap.set(key as ProductNames, r);
            }
        });
        return requestMap;
    }, [applicant?.informationRequests]);

    const [checkingProduct, setCheckingProduct] = useState<ProductNames | null>(null);
    const [checkingAction, setCheckingAction] = useState<ActionType | null>(null);

    const { mutateAsync: generateRecoveryLink, isPending: isGeneratingRecoveryLink } =
        useGenerateRecoveryLinkMutation();

    const { toast, showToast } = useToast('applicant-status-toast');

    // Fetches fresh applicant data and returns true if the action should be blocked.
    // Fail-open: if the fetch errors, allow the action to proceed.
    // Since it's using react-query: it'll also update the disabled status of the buttons if the action was blocked.
    const isActionBlockedForProduct = async (
        id: string,
        product: ProductNames,
        action: ActionType,
    ): Promise<boolean> => {
        setCheckingProduct(product);
        setCheckingAction(action);
        try {
            const fresh = await queryClient.fetchQuery({
                queryKey: ['applicant', id],
                queryFn: () => getApplicant(id),
                staleTime: 0,
            });
            const productData = fresh[ProductNameFields[product]] as
                | FactoringProduct
                | LoadPayProduct;
            const freshWasEverApproved =
                productData?.decisionDetailLinks?.some(
                    (link) =>
                        link.decisionDetail.status === DecisionDetailStatuses.APPROVED &&
                        !link.factoringProductId,
                ) ?? false;
            const freshIsUnderReviewAuto =
                productData?.mostRecentDecisionDetail?.status ===
                DecisionDetailStatuses.UNDERREVIEW_AUTO;

            const blocked =
                action === 'MoreInfo' &&
                isV2InformationRequestEnabled &&
                isPostApprovalInformationRequestsEnabled
                    ? freshIsUnderReviewAuto
                    : freshWasEverApproved || freshIsUnderReviewAuto;

            if (blocked) {
                showToast('error', 'This action is no longer available due to a status update.');

                const userId = authState?.idToken?.claims?.sub;
                const eventName = eventNameMap[action][product];

                honeybadgerLogEvent(`${eventName} - Blocked`, {
                    level: 'WARN',
                    customAttributes: {
                        ...getLoggingContext(currentUserEmail, userId),
                        currentPage: window.location.pathname,
                        timestamp: new Date().toISOString(),
                        applicantId,
                        product,
                    },
                });
            }

            return blocked;
        } finally {
            setCheckingProduct(null);
            setCheckingAction(null);
        }
    };

    const handleDecisionClick = async (product: ProductNames) => {
        if (!applicantId) return;
        if (await isActionBlockedForProduct(applicantId, product, 'Decision')) return;

        const userId = authState?.idToken?.claims?.sub;
        const eventName = eventNameMap['Decision'][product];

        honeybadgerLogEvent(eventName, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId,
                product,
            },
        });

        setDecisionModalProduct(product);
        setShowDecisionModal(true);
    };

    // Helper function to get assignment for a specific product (any assignee, not just current user)
    const getProductAssignment = (product: ProductNames) => {
        if (!assignees || !Array.isArray(assignees)) {
            return null;
        }

        try {
            return (
                assignees.find((assignee) => {
                    if (!assignee || !assignee.email) return false;
                    const assigneeProduct = assignee.product ? String(assignee.product) : '';
                    return assigneeProduct.toUpperCase() === product.toUpperCase();
                }) ?? null
            );
        } catch (error) {
            console.error('Error in getProductAssignment:', error);
            return null;
        }
    };

    const getProductFromApplicant = (product: ProductNames) => {
        return (applicant ? applicant[ProductNameFields[product]] : {}) as
            | FactoringProduct
            | LoadPayProduct;
    };

    const getHasEverBeenApproved = (product: ProductNames) => {
        if (!applicant) return false;

        const productData = getProductFromApplicant(product);

        return (
            productData?.decisionDetailLinks?.some(
                (link) => link.decisionDetail.status === DecisionDetailStatuses.APPROVED,
            ) ?? false
        );
    };

    const getIsCurrentlyDeclined = (product: ProductNames) => {
        if (!applicant) return false;

        const productData = getProductFromApplicant(product);

        return productData?.mostRecentDecisionDetail?.status === DecisionDetailStatuses.DENIED;
    };

    const getHasEverBeenSubmitted = (product: ProductNames) => {
        if (!applicant) return false;

        const productData = getProductFromApplicant(product);

        return (
            productData?.decisionDetailLinks?.some(
                (link) => link.decisionDetail.status === DecisionDetailStatuses.SUBMITTED,
            ) ?? false
        );
    };

    const getSubmittedCount = (product: ProductNames) => {
        if (!applicant) return 0;

        // Shared with the Submissions tab, so a submission's count matches this one.
        return getSubmittedOrdinals(getProductFromApplicant(product)?.decisionDetailLinks).size;
    };

    const getHasEverBeenFinished = (product: ProductNames) => {
        if (!applicant) return false;

        const productData = getProductFromApplicant(product);

        return (
            productData?.decisionDetailLinks?.some(
                (link) => link.decisionDetail.status === DecisionDetailStatuses.FINISHED,
            ) ?? false
        );
    };

    const bothRequestInfoEnabled =
        !!applicant &&
        applicantProductNames.includes(ProductNames.FACTORING) &&
        applicantProductNames.includes(ProductNames.LOADPAY) &&
        !getHasEverBeenApproved(ProductNames.FACTORING) &&
        !getHasEverBeenApproved(ProductNames.LOADPAY) &&
        hasFeature(Feature.NeedsMoreInfo);

    const handleRequestInfoClick = async (product: ProductNames) => {
        if (!applicantId) return;
        if (await isActionBlockedForProduct(applicantId, product, 'MoreInfo')) return;

        const userId = authState?.idToken?.claims?.sub;
        const eventName = eventNameMap['MoreInfo'][product];

        honeybadgerLogEvent(eventName, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId,
                product: bothRequestInfoEnabled ? FACTORING_LOADPAY : product,
            },
        });

        const defaultProd = bothRequestInfoEnabled ? FACTORING_LOADPAY : product;
        setRequestInfoDefaultProduct(defaultProd);

        setShowRequestInfoModal(true);
    };

    const handleEditClick = async (product: ProductNames) => {
        if (!applicantId) return;
        if (await isActionBlockedForProduct(applicantId, product, 'Edit')) return;

        const defaultProduct = bothRequestInfoEnabled ? FACTORING_LOADPAY : product;
        setEditModalDefaultProduct(defaultProduct);

        // Determine which products to generate link for
        const products: ProductNames[] =
            defaultProduct === FACTORING_LOADPAY
                ? [ProductNames.FACTORING, ProductNames.LOADPAY]
                : [defaultProduct as ProductNames];

        const userId = authState?.idToken?.claims?.sub;
        const eventName = eventNameMap['Edit'][product];

        honeybadgerLogEvent(eventName, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId,
                product,
            },
        });

        try {
            // Always generate fresh recovery link
            const response = await generateRecoveryLink({
                applicantId,
                products,
            });

            // Store the recovery link and passcode
            setEditModalRecoveryLink(response.recoveryUrl);
            setEditModalPasscode(response.passcode || '');

            // Only show modal after successful generation
            setShowEditModal(true);
        } catch (error) {
            console.error('Failed to generate recovery link:', error);
            // Could add error toast here if needed
        }
    };

    const renderStatus = () => {
        if (!applicant || applicantProductNames.length === 0) return <></>;

        // A banner rendered before a row means that row shouldn't repeat the divider the banner already draws.
        let previousRowHadBanner = false;

        return applicantProductNames.map((product, index) => {
            const fullProductStatus = `${applicantPartnerId} ${product}`;
            const { mostRecentDecisionDetail } = getProductFromApplicant(product);

            const status = mostRecentDecisionDetail?.status ?? DecisionDetailStatuses.STARTED;

            const submittedCount =
                status === DecisionDetailStatuses.SUBMITTED ? getSubmittedCount(product) : 0;
            const statusLabel =
                submittedCount > 1 ? `${status} (${formatOrdinal(submittedCount)} time)` : status;

            const wasEverApproved = getHasEverBeenApproved(product);
            const isUnderReviewAuto = status === DecisionDetailStatuses.UNDERREVIEW_AUTO;

            const decisionFeature =
                product === ProductNames.LOADPAY
                    ? Feature.DecisionMakingLoadpay
                    : Feature.DecisionMakingFactoring;

            // Get assignment for this specific product
            const productAssignment = getProductAssignment(product);
            // Disable the status buttons (Decision, Edit, NMI) if the product is (Approved AND the product is not Factoring) OR if it's UnderReview_Auto
            const isDisabledActiveReview =
                (wasEverApproved && product !== ProductNames.FACTORING) || isUnderReviewAuto;
            const isDisableProduct = checkingProduct === product || isDisabledActiveReview;
            // Under v2 + post-approval flag, approved products may still receive a new request-info; only block spinning/auto-review
            const isDisableMoreInfo =
                isV2InformationRequestEnabled && isPostApprovalInformationRequestsEnabled
                    ? checkingProduct === product || isUnderReviewAuto
                    : isDisableProduct;

            const recentRequest = isV2InformationRequestEnabled
                ? recentlyCompletedRequestMap.get(product)
                : undefined;

            const showTopBorder = index > 0 && !previousRowHadBanner;
            previousRowHadBanner = !!recentRequest;
            const showFullActions = product !== ProductNames.TCSFUEL;

            return (
                <React.Fragment key={product}>
                    <StatusWrapperRow data-testid={`status-info-${product}`}>
                        <StatusGridCell showTopBorder={showTopBorder}>
                            <InfoTitle>{fullProductStatus}</InfoTitle>
                        </StatusGridCell>
                        <StatusGridCell showTopBorder={showTopBorder}>
                            {status ? (
                                <StatusInfoButton
                                    data-testid="status-info-button"
                                    onClick={() => {
                                        setStatusInfoProductStatus(fullProductStatus);
                                        setStatusInfoStatusLabel(statusLabel);
                                        setStatusInfoDecisionDetail(mostRecentDecisionDetail);
                                        setShowStatusInfoModal(true);
                                    }}
                                >
                                    <Badge
                                        data-testid="status-info-badge"
                                        label={statusLabel}
                                        color={getStatusVariant(status)}
                                        style={{
                                            paddingTop: rem(5),
                                            paddingBottom: rem(5),
                                            height: 'auto',
                                        }}
                                    />
                                </StatusInfoButton>
                            ) : null}
                        </StatusGridCell>
                        <StatusGridCell showTopBorder={showTopBorder}>
                            {openConditionsByProduct[product] > 0 ? (
                                <Tag
                                    data-testid={`status-conditions-count-${product}`}
                                    severity="info"
                                    rounded
                                    value={`• ${openConditionsByProduct[product]} ${
                                        openConditionsByProduct[product] === 1
                                            ? 'condition'
                                            : 'conditions'
                                    }`}
                                    onClick={navigateToConditionsTab}
                                    style={{
                                        fontSize: rem(12),
                                        fontWeight: 600,
                                        padding: `${rem(4)} ${rem(10)}`,
                                        cursor: 'pointer',
                                        ...TAB_BADGE_ACTIVE_STYLE,
                                    }}
                                />
                            ) : null}
                        </StatusGridCell>
                        <StatusGridCell showTopBorder={showTopBorder}>
                            <InfoTitle>
                                {formatDateWithTimezone(
                                    mostRecentDecisionDetail?.dateCreated ?? '',
                                )}
                            </InfoTitle>
                        </StatusGridCell>
                        <StatusGridCell
                            showTopBorder={showTopBorder}
                            style={{ paddingRight: rem(4) }}
                        >
                            {showFullActions ? (
                                <Button
                                    data-testid="status-decision-button"
                                    variant={ButtonVariants.secondary}
                                    onClick={() => handleDecisionClick(product)}
                                    size={ButtonSizes.small}
                                    disabled={isDisableProduct || !hasFeature(decisionFeature)}
                                    title={
                                        !hasFeature(decisionFeature)
                                            ? PERMISSION_DENIED_TITLE
                                            : undefined
                                    }
                                >
                                    🏁 Decision{' '}
                                    {checkingProduct === product &&
                                    checkingAction === 'Decision' ? (
                                        <LoadingIndicator
                                            color="#444"
                                            size={SpinnerSizes.small}
                                            show
                                        />
                                    ) : null}
                                </Button>
                            ) : null}
                        </StatusGridCell>
                        <StatusGridCell
                            showTopBorder={showTopBorder}
                            style={{ paddingLeft: rem(4), paddingRight: rem(4) }}
                        >
                            {showFullActions ? (
                                <Button
                                    data-testid="edit-button"
                                    variant={ButtonVariants.secondary}
                                    onClick={() => handleEditClick(product)}
                                    size={ButtonSizes.small}
                                    disabled={
                                        isDisableProduct ||
                                        !hasFeature(Feature.Edit) ||
                                        isGeneratingRecoveryLink ||
                                        !getHasEverBeenFinished(product)
                                    }
                                    title={!hasFeature(Feature.Edit)
                                            ? PERMISSION_DENIED_TITLE
                                            : undefined
                                    }
                                >
                                    Edit{' '}
                                    {(checkingProduct === product && checkingAction === 'Edit') ||
                                    isGeneratingRecoveryLink ? (
                                        <LoadingIndicator
                                            color="#444"
                                            size={SpinnerSizes.small}
                                            show
                                        />
                                    ) : null}
                                </Button>
                            ) : null}
                        </StatusGridCell>
                        <StatusGridCell
                            showTopBorder={showTopBorder}
                            style={{ paddingLeft: rem(4), paddingRight: rem(4) }}
                        >
                            {showFullActions ? (
                                <Button
                                    data-testid="request-info-button"
                                    variant={ButtonVariants.secondary}
                                    onClick={() => handleRequestInfoClick(product)}
                                    size={ButtonSizes.small}
                                    disabled={
                                        isDisableMoreInfo || !hasFeature(Feature.NeedsMoreInfo)
                                    }
                                    title={
                                        !hasFeature(Feature.NeedsMoreInfo)
                                            ? PERMISSION_DENIED_TITLE
                                            : undefined
                                    }
                                >
                                    ✉️ More Info
                                    {isV2InformationRequestEnabled &&
                                    (activeRequestsByProduct.get(product) ?? []).length > 0 ? (
                                        <PendingRequestBadge>
                                            {(activeRequestsByProduct.get(product) ?? []).length}
                                        </PendingRequestBadge>
                                    ) : null}
                                    {checkingProduct === product &&
                                    checkingAction === 'MoreInfo' ? (
                                        <LoadingIndicator
                                            color="#444"
                                            size={SpinnerSizes.small}
                                            show
                                        />
                                    ) : null}
                                </Button>
                            ) : null}
                        </StatusGridCell>
                        <StatusGridCell
                            showTopBorder={showTopBorder}
                            style={{ paddingLeft: rem(4) }}
                        >
                            <AssignCell
                                applicantId={applicantId!}
                                product={product}
                                currentAssigneeEmail={productAssignment?.email}
                                currentAssigneeId={productAssignment?.id}
                            />
                        </StatusGridCell>
                    </StatusWrapperRow>
                    {recentRequest ? (
                        <RecentlyCompletedRequestBanner data-testid="recently-completed-request-banner">
                            ✅ Recently resubmitted: <strong>{product}</strong>
                            <Button
                                variant={ButtonVariants.secondary}
                                size={ButtonSizes.small}
                                onClick={() => {
                                    setViewRequestModalRequests([recentRequest]);
                                    setShowViewRequestModal(true);
                                }}
                            >
                                View Request
                            </Button>
                        </RecentlyCompletedRequestBanner>
                    ) : null}
                </React.Fragment>
            );
        });
    };

    if (!applicant) {
        return <></>;
    }

    return (
        <StatusWrapper>
            <StatusGrid>{renderStatus()}</StatusGrid>
            <FormProvider {...productDecisionFormMethods}>
                <ProductDecisionModal
                    applicantId={applicant.id}
                    product={decisionModalProduct}
                    showModal={showDecisionModal}
                    setShowModal={setShowDecisionModal}
                    conditions={conditionsData ?? []}
                />
            </FormProvider>
            <FormProvider {...requestInfoFormMethods}>
                <RequestInfoDrawer
                    showDrawer={showRequestInfoModal}
                    setShowDrawer={setShowRequestInfoModal}
                    getHasEverBeenApproved={getHasEverBeenApproved}
                    getHasEverBeenSubmitted={getHasEverBeenSubmitted}
                    getIsCurrentlyDeclined={getIsCurrentlyDeclined}
                    defaultProduct={requestInfoDefaultProduct}
                    isV2InformationRequestEnabled={isV2InformationRequestEnabled}
                    isV2InformationRequestPreApprovalEnabled={
                        isV2InformationRequestPreApprovalEnabled
                    }
                    isCancelInformationRequestEnabled={isCancelInformationRequestEnabled}
                    activeRequests={
                        isV2InformationRequestEnabled || isV2InformationRequestPreApprovalEnabled
                            ? requestInfoActiveRequests
                            : []
                    }
                />
            </FormProvider>
            <StatusInfoModal
                showModal={showStatusInfoModal}
                setShowModal={setShowStatusInfoModal}
                decisionDetail={statusInfoDecisionDetail}
                productStatus={statusInfoProductStatus}
                statusLabel={statusInfoStatusLabel}
            />
            <Modal
                id="view-request-modal"
                headerContent="Recently Resubmitted Request"
                visible={showViewRequestModal}
                onClose={() => setShowViewRequestModal(false)}
                bodyContent={
                    <ModalContainer>
                        {viewRequestModalRequests.map((r) => (
                            <ViewRequestModalCard key={r.id}>
                                <StatusInfoModalRow>
                                    <ViewRequestModalProductTag>
                                        {r.product}
                                    </ViewRequestModalProductTag>
                                    <StatusInfoModalDescription>
                                        Sent {formatDateWithTimezone(r.dateCreated)}
                                    </StatusInfoModalDescription>
                                </StatusInfoModalRow>
                                {r.moreInfoMessage ? (
                                    <ViewRequestModalMessage>
                                        {r.moreInfoMessage}
                                    </ViewRequestModalMessage>
                                ) : null}
                            </ViewRequestModalCard>
                        ))}
                    </ModalContainer>
                }
                primaryButtonText="Close"
                onPrimaryAction={() => setShowViewRequestModal(false)}
            />
            <EditModal
                showModal={showEditModal}
                setShowModal={setShowEditModal}
                defaultProduct={editModalDefaultProduct}
                initialRecoveryLink={editModalRecoveryLink}
                initialPasscode={editModalPasscode}
                getHasEverBeenApproved={getHasEverBeenApproved}
            />
            {toast}
        </StatusWrapper>
    );
};
